import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck, FaCheckCircle, FaEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../PageHeading";
import ConfirmActionModal from "../ConfirmDeleteModal";
import ApproveModal from "../ApproveModal";
import Skeleton from "react-loading-skeleton";
import TableSkeleton from "../TableSkeleton";

const Callback = () => {
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [callbackToDelete, setCallbackToDelete] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [callbackToApprove, setCallbackToApprove] = useState(null);
  const [callbackToView, setCallbackToView] = useState(null);

  const itemsPerPage = 10;
  const API_URL = "https://api.muktihospital.com";
  const API_KEY =
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  // Fetch callback data
  const fetchCallbacks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/callback`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
      });

      const result = await response.json();
      if (response.ok) {
        const sortedCallbacks = [...(result.callbacks || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setCallbacks(sortedCallbacks);
        setSelectedRows(new Array(result.callbacks?.length || 0).fill(false));
        setError(null);
      } else {
        setError(result.message || "Failed to fetch callback data.");
        toast.error(result.message || "Failed to fetch callback data.");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("An error occurred while fetching callback data.");
      toast.error("An error occurred while fetching callback data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCallbacks();
  }, []);

  // Filter callbacks based on search term
  const filterCallbacks = () => {
    return callbacks.filter((callback) => {
      const name = callback.patientName?.toLowerCase() || "";
      const phone = callback.phone?.toLowerCase() || "";
      const date = new Date(callback.createdAt)
        .toLocaleDateString()
        .toLowerCase();
      return (
        name.includes(searchTerm.toLowerCase()) ||
        phone.includes(searchTerm.toLowerCase()) ||
        date.includes(searchTerm.toLowerCase())
      );
    });
  };

  // Pagination logic
  const filteredData = filterCallbacks();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Debug state changes
  useEffect(() => {
    // console.log("Callbacks updated:", callbacks);
    // console.log("Current Page:", currentPage);
    // console.log("Filtered Data:", filteredData);
    // console.log("Total Pages:", totalPages);
    // console.log("Current Data:", currentData);
  }, [callbacks, currentPage, filteredData, totalPages, currentData]);

  // Adjust page if currentPage exceeds totalPages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Update header checkbox state
  useEffect(() => {
    if (currentData.length > 0) {
      setIsHeaderChecked(
        currentData.every((_, idx) => {
          const globalIndex = callbacks.findIndex(
            (callback) => callback.id === currentData[idx].id
          );
          return globalIndex !== -1 && selectedRows[globalIndex];
        })
      );
    } else {
      setIsHeaderChecked(false);
    }
  }, [selectedRows, currentData, callbacks]);

  // Reset selected rows when callbacks change
  useEffect(() => {
    setSelectedRows(new Array(callbacks.length).fill(false));
    setIsHeaderChecked(false);
  }, [callbacks]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      // console.log(`Changing page to ${page}`);
      setCurrentPage(page);
    }
  };

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    currentData.forEach((_, idx) => {
      const globalIndex = callbacks.findIndex(
        (callback) => callback.id === currentData[idx].id
      );
      if (globalIndex !== -1) {
        newSelectedRows[globalIndex] = !isHeaderChecked;
      }
    });
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!isHeaderChecked);
  };

  const handleRowCheckboxChange = (globalIndex) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[globalIndex] = !newSelectedRows[globalIndex];
    setSelectedRows(newSelectedRows);
  };

  // Delete single or multiple callbacks
  const handleDeleteCallback = async (ids) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    if (idArray.length === 0) return;

    // console.log("Deleting callbacks with IDs:", idArray);

    try {
      // Perform the delete requests
      const results = await Promise.allSettled(
        idArray.map((id) =>
          fetch(`${API_URL}/api/callback/${id}`, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": API_KEY,
            },
          })
        )
      );

      // Check for failed delete requests
      const failedRequests = results.filter(
        (result) => result.status === "rejected" || !result.value.ok
      );

      if (failedRequests.length > 0) {
        failedRequests.forEach((result, index) => {
          console.error(`Delete failed for ID ${idArray[index]}:`, result.reason);
        });
        throw new Error("Some delete requests failed.");
      }

      // Optimistically update callbacks
      const updatedCallbacks = callbacks.filter(
        (callback) => !idArray.includes(String(callback.id))
      );
      // console.log("Updated callbacks:", updatedCallbacks);

      // Calculate new total pages based on filtered data
      const newFilteredData = updatedCallbacks.filter((callback) => {
        const name = callback.patientName?.toLowerCase() || "";
        const phone = callback.phone?.toLowerCase() || "";
        const date = new Date(callback.createdAt)
          .toLocaleDateString()
          .toLowerCase();
        return (
          name.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm.toLowerCase()) ||
          date.includes(searchTerm.toLowerCase())
        );
      });
      const newTotalPages = Math.ceil(newFilteredData.length / itemsPerPage);

      // Adjust current page
      let newCurrentPage = currentPage;
      if (currentPage > newTotalPages && newTotalPages > 0) {
        newCurrentPage = newTotalPages;
      } else if (newTotalPages === 0) {
        newCurrentPage = 1;
      }

      // Update state
      setCallbacks([...updatedCallbacks]); // Ensure new array reference
      setCurrentPage(newCurrentPage);
      setSelectedRows(new Array(updatedCallbacks.length).fill(false));
      setIsHeaderChecked(false);

      toast.success(
        `Callback${idArray.length > 1 ? "s" : ""} deleted successfully`
      );

      // Fallback: Refetch data to ensure consistency
      await fetchCallbacks();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete callback(s). Refetching data...");
      await fetchCallbacks(); // Refetch on error to ensure state consistency
    } finally {
      closeDeleteModal();
    }
  };

  // Complete callback
  const handleCompleteCallback = async (callbackId, note) => {
    try {
      const originalCallbacks = [...callbacks];
      setCallbacks((prevCallbacks) =>
        prevCallbacks.map((callback) =>
          callback.id === callbackId
            ? { ...callback, status: "complete", note }
            : callback
        )
      );

      const response = await fetch(
        `${API_URL}/api/callback/${callbackId}/approve`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
          },
          body: JSON.stringify({ note }),
        }
      );

      const result = await response.json();
      if (response.ok) {
        await fetchCallbacks();
        toast.success("Callback completed successfully");
      } else {
        setCallbacks(originalCallbacks);
        throw new Error(result.message || "Failed to complete callback");
      }
    } catch (err) {
      console.error("Complete error:", err);
      toast.error("Failed to complete callback");
    } finally {
      closeApproveModal();
    }
  };

  const openDeleteModal = (callbackIds) => {
    setCallbackToDelete(callbackIds);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCallbackToDelete(null);
  };

  const openApproveModal = (callbackId) => {
    setCallbackToApprove(callbackId);
    setApproveModalOpen(true);
  };

  const closeApproveModal = () => {
    setApproveModalOpen(false);
    setCallbackToApprove(null);
  };

  const openViewModal = (callback) => {
    setCallbackToView(callback);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setCallbackToView(null);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Callback Menu"},
    { label: "Callback Submissions" },
  ];

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString))) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const skeletonColumns = [
    { width: 20, height: 20, isCircle: false, className: "pl-3" },
    { width: 80, height: 20, className: "px-3" },
    { width: 150, height: 20, className: "px-3" },
    { width: 120, height: 20, className: "px-3" },
    { width: 100, height: 20, className: "px-3" },
    { width: 80, height: 20, className: "px-3" },
    { width: 80, height: 20, className: "px-3" },
    { width: 80, height: 20, className: "px-3 text-center" },
  ];

  // View Modal Component
  const ViewModal = ({ isOpen, onClose, callback }) => {
    if (!isOpen || !callback) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
        <div className="bg-white rounded-xl overflow-hidden shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100">
          <div className="flex flex-wrap gap-3 flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 pb-2 print-hidden px-4 py-4">
            <h2 className="text-lg font-medium text-black">
              Callback Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-3 p-4">
            <div>
              <p className="text-sm font-bold text-black">Patient Name:</p>
              <p className="text-M-text-color">{callback.patientName || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-bold text-black">Phone:</p>
              <p className="text-M-text-color">{callback.phone || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-bold text-black">Request Date:</p>
              <p className="text-M-text-color">{formatDate(callback.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm font-bold text-black">Contact Date:</p>
              <p className="text-M-text-color">{formatDate(callback.updatedAt)}</p>
            </div>
            <div>
              <p className="text-sm font-bold text-black">Status:</p>
              <p className={`${callback.status === "complete" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-600"} inline-block px-2 py-1 rounded-full text-xs capitalize`}>
                {callback.status || "Pending"}
              </p>
            </div>
            <div>
              <p className="text-sm font-bold text-black">Completion Note:</p>
              <p className="text-M-text-color capitalize">{callback.note || "No note provided"}</p>
            </div>
          </div>

          <div className="flex justify-end mt-6 px-4 pb-4">
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <ToastContainer position="top-right" />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteCallback}
        ids={callbackToDelete}
        entityName="callback"
        title="Confirm Deletion"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ApproveModal
        isOpen={approveModalOpen}
        onClose={closeApproveModal}
        callbackId={callbackToApprove}
        onApprove={handleCompleteCallback}
      />
      <ViewModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        callback={callbackToView}
      />
      <PageHeading title="Callback Submissions" breadcrumbs={breadcrumbs} />
      {error && <div className="text-red-600 mb-4">{error}</div>}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Callback Request List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedCallbackIds = callbacks
                    .map((callback, index) =>
                      selectedRows[index] ? callback.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedCallbackIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            {loading ? (
              <Skeleton height={40} width={192} />
            ) : (
              <input
                type="text"
                className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
                placeholder="Search by name, phone, or date"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left">
                  <div className="relative left-3 -top-[3px]">
                    <input
                      type="checkbox"
                      id="header-checkbox"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="header-checkbox"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  SL
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Patient Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Phone
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Request Date
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Status
                </th>
                <th className="text-center uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                  baseColor="#e5e7eb"
                  highlightColor="#f3f4f6"
                />
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center p-4">
                    No callback submissions available
                  </td>
                </tr>
              ) : (
                currentData.map((callback, index) => {
                  const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
                  const callbackId =
                    callback.id != null ? String(callback.id) : "N/A";
                  const globalIndex = callbacks.findIndex(
                    (c) => c.id === callback.id
                  );
                  return (
                    <tr key={callbackId} className="border-b hover:bg-gray-50">
                      <td className="text-left">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={`callback-${globalIndex}`}
                            className="hidden peer"
                            checked={selectedRows[globalIndex] || false}
                            onChange={() => handleRowCheckboxChange(globalIndex)}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`callback-${globalIndex}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {serialNumber}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {callbackId !== "N/A" ? callbackId.slice(0, 8) : "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {callback.patientName || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {callback.phone || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {formatDate(callback.createdAt)}
                      </td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${callback.status === "complete" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-600"}`}>
                          {callback.status || "Pending"}
                        </span>
                      </td>
                      <td className="p-3 text-center space-x-2">
                        {callback.status !== "complete" ? (
                          <button
                            onClick={() => openApproveModal(callbackId)}
                            className="size-7 rounded-full bg-green-100 text-green-600 text-sm inline-flex items-center justify-center hover:bg-green-600 hover:text-white transition-all duration-300"
                            title="Complete"
                          >
                            <FaCheckCircle />
                          </button>
                        ) : (
                          <button
                            onClick={() => openViewModal(callback)}
                            className="size-7 rounded-full bg-blue-100 text-blue-600 text-sm inline-flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                        )}
                        <button
                          onClick={() => openDeleteModal([callbackId])}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && totalPages > 1 && (
          <div className="p-5 text-end">
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
              <li>
                <button
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
              </li>
              {totalPages <= 10
                ? [...Array(totalPages)].map((_, i) => (
                    <li key={i}>
                      <button
                        className={`table-pagination-item ${
                          currentPage === i + 1
                            ? "bg-M-primary-color text-white"
                            : ""
                        }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))
                : (() => {
                    const pages = [];
                    const firstPages = 5;
                    const lastPages = 3;

                    for (
                      let i = 1;
                      i <= Math.min(firstPages, totalPages);
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    if (totalPages > firstPages + lastPages) {
                      pages.push(
                        <li key="ellipsis" className="table-pagination-item">
                          ...
                        </li>
                      );
                    }

                    for (
                      let i = Math.max(
                        firstPages + 1,
                        totalPages - lastPages + 1
                      );
                      i <= totalPages;
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}
              <li>
                <button
                  className="table-pagination-item"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <MdKeyboardArrowRight />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Callback;